import { DEFAULT_OFFERS_BACKGROUND_URL } from 'constants/constants';
import { OfferType } from 'constants/enums';
import Decimal from 'decimal.js';

import type {
  BuildCurrentBundleParams,
  CurrentBundle,
  DynamicOfferUi,
  FormValues,
  OfferData,
  OfferUi,
  OfferUIData,
  PreviewProduct,
  PriceDiscount,
  ProductSale,
  ProductSequence
} from '../types';

const buildOfferUi = (
  values: FormValues,
  getOfferUI: OfferUIData
): OfferUi => ({
  backgroundImage:
    getOfferUI?.backgroundImage ||
    values.backgroundImage ||
    DEFAULT_OFFERS_BACKGROUND_URL,
  offerUiType: OfferType.BUNDLE,
  bundle: {
    templateType: getOfferUI?.bundle?.templateType || 'Single',
    backgroundColor: getOfferUI?.bundle?.backgroundColor || {
      colorOne: '#323281'
    }
  },
  borderColor: getOfferUI?.borderColor || {
    colorOne: '#ffffff',
    colorTwo: '',
    direction: ''
  },
  borderWidth: getOfferUI?.borderWidth || 2
});

const buildDynamicOfferUi = (
  values: FormValues,
  emblemBadges: unknown[],
  ribbonBadges: unknown[]
): DynamicOfferUi => ({
  badges: [],
  salePercentage: values.salePercentage || 0,
  salePercentageDisplayType: values.salePercentageDisplayType || 'percentage',
  offerBadges: {
    emblems: {
      data: emblemBadges || []
    },
    ribbons: {
      data: ribbonBadges || []
    }
  }
});

const buildProductSale = (values: FormValues): ProductSale => ({
  sale: values.salePercentage || 0,
  type: values.salePercentageDisplayType || 'percentage'
});

const buildPriceDiscount = (values: FormValues): PriceDiscount => ({
  discount: values.discount || 0,
  type: values.priceDiscountDisplayType || 'percentage'
});

const buildProductsSequence = (
  values: FormValues,
  productsListForPreview: PreviewProduct[],
  transformPriceValue: () => number
): ProductSequence[] => {
  const hasProductSale = (values.salePercentage ?? 0) > 0;
  const hasPreviewProducts =
    hasProductSale && productsListForPreview.length > 0;

  const products = hasPreviewProducts
    ? productsListForPreview.map((product, index) => {
        if (index !== 0) return product;

        const strokedSaleQuantity = String(
          product.quantity ?? product.quantityDisplay ?? ''
        );

        return {
          ...product,
          strokedSaleQuantity
        };
      })
    : productsListForPreview;

  return [
    {
      index: 1,
      products,
      price: {
        currencyCode: 'USD',
        currencySymbol: '$',
        price: values.price || 0,
        symbolPosition: 'Left',
        spacing: false,
        fractionalSeparator: '.',
        milSeparator: ',',
        isCents: true,
        multiplier: 100
      },
      priceInUsdCents: values.price
        ? new Decimal(values.price).mul(100).toNumber()
        : 0,
      prices: {
        priceInUsdCents: values.price
          ? new Decimal(values.price).mul(100).toNumber()
          : 0,
        priceDisplay: `$${transformPriceValue()}`,
        currencyCode: 'USD'
      }
    }
  ];
};

export const buildCurrentBundle = ({
  values,
  getOfferUI,
  productsListForPreview,
  badges,
  emblemBadges,
  ribbonBadges,
  transformPriceValue
}: BuildCurrentBundleParams): CurrentBundle => ({
  offerId: values.offerId || 'preview',
  publisherOfferId: values.publisherOfferId || '',
  offerJwt: '',
  offerType: OfferType.BUNDLE,
  offerName: values.name || '',
  displayName: values.displayName || values.name || '',
  description: values.description,
  offerUi: buildOfferUi(values, getOfferUI),
  dynamicOfferUi: buildDynamicOfferUi(values, emblemBadges, ribbonBadges),
  badges: badges || [],
  productSale: buildProductSale(values),
  priceDiscount: buildPriceDiscount(values),
  productsSequence: buildProductsSequence(
    values,
    productsListForPreview,
    transformPriceValue
  ),
  offerBadges: {
    emblems: emblemBadges || [],
    ribbons: ribbonBadges || []
  },
  sectionId:
    values.section && values.section !== '0' ? values.section : undefined,
  tabId: values.tab && values.tab !== '0' ? values.tab : undefined,
  segments: values.segments,
  tags: values.tags
});

export const buildOfferData = (currentBundle: CurrentBundle): OfferData => ({
  offers: {
    progressBars: [],
    popups: [],
    promotions: [],
    bundles: [currentBundle],
    rolling: []
  },
  balances: [],
  offerFocus: {},
  sections: [],
  profileFrameImg: '',
  playerLevelImg: '',
  publisherSettings: {
    popUpStartHourUTC: 0
  },
  bannerImage: '',
  playerLevels: [],
  tabs: [],
  logo: ''
});
